/**
 * ReportsPage - relatórios analíticos
 */
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import KPICard from "@/components/shared/KPICard";
import { Eye, Heart, MessageSquare, FileDown, Share2, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import {
    LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend,
} from "recharts";
import ChannelIcon from "@/components/shared/ChannelIcon";
import { CHANNELS } from "@/lib/channels";
import { fmtDateTime } from "@/lib/date";
import {
    Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const PIE_COLORS = ["#A78BFA", "#60A5FA", "#2DD4BF", "#FBBF24", "#FB7185", "#7C3AED"];

export default function ReportsPage() {
    const [data, setData] = useState(null);
    const [days, setDays] = useState(30);

    useEffect(() => {
        api.get(`/reports/overview?days=${days}`).then((r) => setData(r.data));
    }, [days]);

    const k = data?.kpi || {};
    const series = data?.series || [];
    const channelData = (data?.by_channel || []).map((c) => {
        const meta = CHANNELS.find((m) => m.type === c.channel);
        return { ...c, name: meta?.name || c.channel };
    });

    const exportCsv = () => {
        if (!data) return;
        let rows = [["Data", "Alcance", "Engagement", "Posts"]];
        for (const r of series) rows.push([r.date, r.reach, r.engagement, r.posts]);
        const csv = rows.map((r) => r.join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `mesafa-relatorio-${days}d.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("CSV exportado");
    };

    return (
        <div className="space-y-6" data-testid="reports-page">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">Relatórios</h1>
                    <p className="text-muted-foreground">Análise consolidada de performance.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
                        <SelectTrigger className="w-40" data-testid="reports-date-range-picker"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="7">Últimos 7 dias</SelectItem>
                            <SelectItem value="30">Últimos 30 dias</SelectItem>
                            <SelectItem value="90">Últimos 90 dias</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button variant="outline" onClick={exportCsv} data-testid="reports-export-csv-button">
                        <FileDown className="h-4 w-4 mr-2" /> Exportar CSV
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KPICard icon={Eye} label="Alcance total" value={(k.total_reach || 0).toLocaleString("pt-PT")} accent="#60A5FA" testid="reports-kpi-reach" />
                <KPICard icon={Heart} label="Engagement" value={(k.total_engagement || 0).toLocaleString("pt-PT")} accent="#FB7185" testid="reports-kpi-engagement" />
                <KPICard icon={TrendingUp} label="Taxa de eng." value={`${k.avg_engagement_rate || 0}%`} accent="#2DD4BF" testid="reports-kpi-rate" />
                <KPICard icon={Share2} label="Publicadas" value={k.total_published || 0} accent="#A78BFA" testid="reports-kpi-published" />
            </div>

            <div className="grid lg:grid-cols-2 gap-4">
                <Card className="glass-card p-5 rounded-2xl">
                    <h3 className="font-display font-semibold mb-4">Alcance ao longo do tempo</h3>
                    <div className="h-[260px]" data-testid="reports-engagement-line-chart">
                        <ResponsiveContainer>
                            <LineChart data={series} margin={{ left: -20, right: 6 }}>
                                <CartesianGrid stroke="rgba(255,255,255,0.06)" />
                                <XAxis dataKey="date" stroke="#A7A9C0" fontSize={11} />
                                <YAxis stroke="#A7A9C0" fontSize={11} />
                                <Tooltip contentStyle={{ background: "#141625", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#EDEDF7" }} />
                                <Line type="monotone" dataKey="reach" stroke="#A78BFA" strokeWidth={2} dot={{ r: 3 }} />
                                <Line type="monotone" dataKey="engagement" stroke="#2DD4BF" strokeWidth={2} dot={{ r: 3 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </Card>

                <Card className="glass-card p-5 rounded-2xl">
                    <h3 className="font-display font-semibold mb-4">Por canal</h3>
                    <div className="h-[260px]">
                        <ResponsiveContainer>
                            <BarChart data={channelData}>
                                <CartesianGrid stroke="rgba(255,255,255,0.06)" />
                                <XAxis dataKey="name" stroke="#A7A9C0" fontSize={11} />
                                <YAxis stroke="#A7A9C0" fontSize={11} />
                                <Tooltip contentStyle={{ background: "#141625", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#EDEDF7" }} />
                                <Bar dataKey="reach" fill="#A78BFA" radius={[8, 8, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
            </div>

            <div className="grid lg:grid-cols-3 gap-4">
                <Card className="glass-card p-5 rounded-2xl lg:col-span-1">
                    <h3 className="font-display font-semibold mb-4">Distribuição</h3>
                    <div className="h-[230px]">
                        <ResponsiveContainer>
                            <PieChart>
                                <Pie data={channelData} dataKey="reach" nameKey="name" innerRadius={50} outerRadius={80}>
                                    {channelData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                                </Pie>
                                <Tooltip contentStyle={{ background: "#141625", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#EDEDF7" }} />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                </Card>
                <Card className="glass-card p-5 rounded-2xl lg:col-span-2">
                    <h3 className="font-display font-semibold mb-3">Top publicações</h3>
                    {(data?.top_posts || []).length === 0 ? (
                        <div className="text-sm text-muted-foreground py-8 text-center">Sem dados para mostrar.</div>
                    ) : (
                        <ul className="divide-y divide-white/5">
                            {data.top_posts.map((p) => (
                                <li key={p.id} className="py-3 flex items-center gap-3">
                                    {p.media?.[0]?.url && <img src={p.media[0].url} alt="" className="h-12 w-12 rounded-lg object-cover" />}
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm line-clamp-1">{p.caption}</p>
                                        <div className="text-xs text-muted-foreground flex items-center gap-2">
                                            {p.channels?.slice(0, 2).map((ch) => <ChannelIcon key={ch} type={ch} className="h-3 w-3" />)}
                                            · {fmtDateTime(p.published_at)}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <Badge className="bg-[#6B21D9]/15 text-[#A78BFA] border-[#6B21D9]/30 border">
                                            {p.metrics?.engagement || 0} eng.
                                        </Badge>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </Card>
            </div>
        </div>
    );
}
